import React from 'react';
import { Sparkles, Sliders, Database, Eye, EyeOff, ArrowUp, ArrowDown, HelpCircle, CheckSquare, Square } from 'lucide-react';
import { PageSection } from '../../../../types';

interface SearchFieldConfig {
  id: string;
  name: string;
  label: string;
  placeholder?: string;
  visible: boolean;
  required: boolean;
  type: 'select' | 'date-range' | 'number' | 'price-range';
}

interface YachtFilterEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

const DEFAULT_SEARCH_FIELDS: SearchFieldConfig[] = [
  {
    id: 'port',
    name: 'Çıkış Limanı / Marina',
    label: 'Çıkış Limanı',
    placeholder: 'Tüm Limanlar',
    visible: true,
    required: false,
    type: 'select',
  },
  {
    id: 'dates',
    name: 'Tarih Aralığı (Alış - İade)',
    label: 'Tur Tarihleri',
    placeholder: 'Tarih Seçin',
    visible: true,
    required: false,
    type: 'date-range',
  },
  {
    id: 'yachtType',
    name: 'Yat / Tekne Tipi',
    label: 'Yat Tipi',
    placeholder: 'Tüm Tipler',
    visible: true,
    required: false,
    type: 'select',
  },
  {
    id: 'guests',
    name: 'Misafir / Kişi Sayısı',
    label: 'Kişi Sayısı',
    placeholder: 'Kişi sayısı',
    visible: true,
    required: false,
    type: 'number',
  },
  {
    id: 'cabins',
    name: 'Kabin Sayısı',
    label: 'Min. Kabin',
    placeholder: 'Kabin sayısı',
    visible: true,
    required: false,
    type: 'number',
  },
  {
    id: 'price',
    name: 'Maks. Günlük Fiyat (€)',
    label: 'Maks. Bütçe',
    placeholder: 'Bütçe sınırı',
    visible: true,
    required: false,
    type: 'price-range',
  },
];

export const YachtFilterEditor: React.FC<YachtFilterEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};

  const fields: SearchFieldConfig[] = content.fields && Array.isArray(content.fields) && content.fields.length > 0
    ? content.fields
    : DEFAULT_SEARCH_FIELDS;

  const handleUpdateField = (index: number, updates: Partial<SearchFieldConfig>) => {
    const nextFields = [...fields];
    nextFields[index] = { ...nextFields[index], ...updates };
    onUpdateContent({ fields: nextFields });
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    const nextFields = [...fields];
    const [moved] = nextFields.splice(index, 1);
    nextFields.splice(targetIndex, 0, moved);
    onUpdateContent({ fields: nextFields });
  };

  return (
    <div className="space-y-6">
      {/* 1. Real Data Source Info Banner (Requirement 55 & 57) */}
      <div className="p-4 rounded-2xl bg-teal-950/40 border border-teal-800/60 space-y-2">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-teal-400" />
          <h4 className="text-xs font-bold text-teal-200">Gerçek Veri Kaynağı (Yat Filosu)</h4>
        </div>
        <p className="text-[11px] text-teal-300/80 leading-relaxed">
          Bu arama motoru doğrudan sitenizin <strong>Yat Filosu</strong>, <strong>Müsaitlik Takvimi</strong> ve <strong>Marinalar</strong> veritabanına bağlıdır. Ayrı yat eklenemez; girilen arama kriterlerine uyan gerçek yatlar dinamik olarak listelenir.
        </p>
      </div>

      {/* 2. Titles & Copy */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Başlık ve Metinler</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'Müsaitlik ve Rezervasyon'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            placeholder="Örn: Müsaitlik ve Rezervasyon"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Mavi Tur & Yat Arama'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Arama Bölümü Başlığı..."
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Alt Açıklama</label>
          <textarea
            value={content.subtitle ?? 'Tarih, liman, kişi sayısı ve tekne tipine göre filomuzda arama yapın'}
            onChange={(e) => onUpdateContent({ subtitle: e.target.value })}
            rows={2}
            placeholder="Kullanıcıyı yönlendiren açıklama..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Arama Butonu Yazısı</label>
            <input
              type="text"
              value={content.searchButtonText ?? 'Müsait Yatları Ara'}
              onChange={(e) => onUpdateContent({ searchButtonText: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-1.5 rounded-xl outline-none focus:border-blue-500"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Sıfırla Butonu Yazısı</label>
            <input
              type="text"
              value={content.resetButtonText ?? 'Filtreleri Temizle'}
              onChange={(e) => onUpdateContent({ resetButtonText: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-1.5 rounded-xl outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* 3. Search Fields Management (Requirement 56) */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sliders className="w-3.5 h-3.5 text-teal-400" />
            <h4 className="text-xs font-bold text-slate-200">Arama Alanları Yönetimi</h4>
          </div>
          <span className="text-[10px] font-semibold text-slate-400">{fields.filter((f) => f.visible).length} Aktif Alan</span>
        </div>
        <p className="text-[11px] text-slate-400">
          Arama kutusundaki alanları gösterin/gizleyin, sıralayın, etiketlerini ve zorunluluk durumlarını özelleştirin:
        </p>

        <div className="space-y-2.5">
          {fields.map((field, idx) => (
            <div
              key={field.id}
              className={`p-3 rounded-xl border transition-all ${
                field.visible
                  ? 'bg-slate-900/80 border-slate-700/80 text-slate-200'
                  : 'bg-slate-900/30 border-slate-800 text-slate-500 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={() => handleUpdateField(idx, { visible: !field.visible })}
                    title={field.visible ? 'Gizle' : 'Göster'}
                    className={`p-1 rounded-lg border transition-colors ${
                      field.visible
                        ? 'bg-teal-500/20 text-teal-400 border-teal-500/30 hover:bg-teal-500/30'
                        : 'bg-slate-800 text-slate-500 border-slate-700 hover:text-slate-300'
                    }`}
                  >
                    {field.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <span className="text-xs font-bold truncate">{field.name}</span>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => handleMoveField(idx, 'up')}
                    className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                    title="Yukarı Taşı"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={idx === fields.length - 1}
                    onClick={() => handleMoveField(idx, 'down')}
                    className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                    title="Aşağı Taşı"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Field customization row */}
              {field.visible && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-slate-800/80">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Etiket (Label)</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => handleUpdateField(idx, { label: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 text-[11px] text-slate-200 px-2 py-1 rounded-lg outline-none focus:border-teal-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Placeholder</label>
                    <input
                      type="text"
                      value={field.placeholder ?? ''}
                      onChange={(e) => handleUpdateField(idx, { placeholder: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 text-[11px] text-slate-200 px-2 py-1 rounded-lg outline-none focus:border-teal-500"
                    />
                  </div>

                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-semibold text-slate-300">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => handleUpdateField(idx, { required: e.target.checked })}
                        className="rounded border-slate-700 bg-slate-950 text-teal-500 focus:ring-0"
                      />
                      <span>Zorunlu Alan</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
